import { diaryByIdQueryOptions } from "@/features/diaries/api";
import { resolveTrackerDueEntry } from "@/features/diaries/utils/findEarliestDueEntry";
import { extractTrackerById } from "@/features/diaries/services/extractTrackerById";
import { isTrackerBlockedBySequence } from "@/features/diaries/utils/sequentialFormHelpers";
import { redirectIfConsentRequired } from "@/features/consent/utils/redirectIfConsentRequired";
import PatientsTrackerFormPage from "@/features/pages/components/PatientsTrackerFormPage";
import { formatTrackerLateralityBanner } from "@/features/pages/utils/trackerLateralityBanner";
import { useAppLocale } from "@/features/i18n/LocaleProvider";
import { useWorklistCopy } from "@/features/i18n/worklistCopy";
import { WorklistPanelHeader } from "@/features/worklist/components/WorklistPanelHeader";
import type { FormViewT } from "@patient-watch/types";
import { createFileRoute, redirect } from "@tanstack/react-router";

const Page = () => {
    const copy = useWorklistCopy();
    const { tracker, patientName } = Route.useLoaderData();
    const { pageIndex, diaryId } = Route.useParams();
    const { locale } = useAppLocale();
    const lateralityBanner = formatTrackerLateralityBanner(tracker?.side, locale);
    const taskLabel = tracker?.form?.form_name ?? copy.forms;

    return (
        <div className="flex flex-col gap-4 p-2 md:p-3 pb-4" data-testid="worklist-form-panel">
            <WorklistPanelHeader title={taskLabel} subtitle={patientName} diaryId={diaryId} />
            <PatientsTrackerFormPage
                pageIndex={Number(pageIndex)}
                form={tracker?.form as FormViewT}
                route={Route}
                nextEntry={tracker ? resolveTrackerDueEntry(tracker) : null}
                diaryId={diaryId}
                lateralityBanner={lateralityBanner}
            />
        </div>
    );
};

export const Route = createFileRoute("/_app/worklist/form/$diaryId/$trackerId/$entryId/page/$pageIndex")({
    component: Page,
    loader: async ({ context, params }) => {
        const diary = await context?.queryClient.ensureQueryData(diaryByIdQueryOptions(params.diaryId));
        if (!diary) {
            throw new Error("Diary not found");
        }
        const tracker = extractTrackerById(diary, params.trackerId);
        const isPatientViewer = context.auth?.id === diary.patient_id || context.auth?.roles?.isPatientOnly === true;

        redirectIfConsentRequired(diary, context.auth?.id, isPatientViewer);

        if (isTrackerBlockedBySequence(tracker)) {
            throw redirect({ to: "/worklist" });
        }

        return {
            patientId: diary.patient_id ?? "",
            patientName: diary.patient_info?.patient_name ?? "Patient",
            tracker: tracker,
        };
    },
});
